import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStore } from 'logic';
import { fn } from 'firebase-modules';
import { designTokens } from 'theme';

const DESCRIPTION_MAX_LENGTH = 500;

const JAM_RECORDING_LINKS: Record<string, string> = {
  'app.creativequeue.io': 'https://app.creativequeue.io/?jam-recording=J2e6AAW',
  'cq-dev.sparkprojects.vercel.app':
    'https://cq-dev.sparkprojects.vercel.app/?jam-recording=nS5u1mv',
};
const JAM_FALLBACK_URL = 'https://recorder.jam.dev/KfhFNB6';

function getJamRecordingUrl(): string {
  return JAM_RECORDING_LINKS[window.location.hostname] ?? JAM_FALLBACK_URL;
}

interface ReportBugModalProps {
  open: boolean;
  onClose: () => void;
}

export const ReportBugModal = observer(({ open, onClose }: ReportBugModalProps) => {
  const {
    authStore: { user },
    uiSnackbarStore,
  } = useStore();

  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descriptionError =
    description.length > DESCRIPTION_MAX_LENGTH
      ? `Maximum ${DESCRIPTION_MAX_LENGTH} characters`
      : null;
  const canSubmit =
    description.trim().length > 0 && description.length <= DESCRIPTION_MAX_LENGTH && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || !user) return;

    // Open recording tab synchronously to avoid browser popup blocker
    // (window.open after an await is no longer a trusted user gesture)
    window.open(getJamRecordingUrl(), '_blank', 'noopener,noreferrer');

    setIsSubmitting(true);
    setError(null);

    try {
      const submitBugReportFn = fn.httpsCallable('submitBugReport');
      await submitBugReportFn({ description: description.trim() });

      uiSnackbarStore.setSnackbarDetails({
        description: 'Bug report submitted.',
      });

      setDescription('');
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit bug report';
      setError(message);
      console.error('Failed to submit bug report:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setDescription('');
    setError(null);
    onClose();
  };

  const userName = user?.displayName || user?.email || 'No Name';
  const userEmail = user?.email || 'No email';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      PaperProps={{
        sx: {
          bgcolor: designTokens.colors.bg.card,
          border: `1px solid ${designTokens.colors.border.subtle}`,
        },
      }}
    >
      <DialogTitle sx={{ color: designTokens.colors.text.primary }}>Report Bug</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontSize: designTokens.typography.fontSize.sm }}
        >
          Describe the bug you encountered. Your name and email will be included for follow-up.
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Bug description"
          placeholder="What happened? What did you expect?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSubmitting}
          error={!!descriptionError}
          helperText={descriptionError || `${description.length}/${DESCRIPTION_MAX_LENGTH}`}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Your name"
            value={userName}
            disabled
            InputProps={{ readOnly: true }}
          />
          <TextField
            fullWidth
            size="small"
            label="Your email"
            value={userEmail}
            disabled
            InputProps={{ readOnly: true }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 2, color: designTokens.colors.text.muted }}
        >
          Tip: Select "Chrome Tab" when prompted to include console logs. Add your name in the
          recording for easier correlation.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Saving...' : 'Save and Record Video'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
